import { useEffect } from 'react';
import { useRecoilState } from 'recoil';

import { USER_SETTINGS_KEY } from '@constants';
import { userSettingsState } from '@store';

import { useGetGeoLocation } from './useGetGeoLocation';

import { getStorageItem, setStorageItem } from '../helpers/storage';

export const useUserSettings = () => {
  const [userSettings, setUserSettingsState] = useRecoilState(userSettingsState);

  const geoLocation = useGetGeoLocation();

  useEffect(() => {
    // if the user has no locations, then they are a first time user
    // when the geo location comes back, add it to their list and set as default
    if (
      userSettings.fetchedFromStorage &&
      geoLocation?.coords &&
      geoLocation.coords.lat &&
      geoLocation.coords.lng &&
      userSettings.locations.length === 1 &&
      userSettings.locations[0]?.id === 0 // tells us that this is the empty location from atom
    ) {
      const newSettings = { ...userSettings };

      newSettings.locations = [
        {
          id: 1,
          fullName: `${geoLocation.city}, ${geoLocation.state}`,
          name: `${geoLocation.city}, ${geoLocation.state}`,
          city: geoLocation.city,
          state: geoLocation.state,
          isWatching: true,
          isDefault: true,
          coords: {
            ...geoLocation.coords,
          },
          zipCode: geoLocation.zipCode,
          market: geoLocation.market,
          timeZone: geoLocation.timeZone,
        },
      ];
      setUserSettingsState(newSettings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoLocation, userSettings]);

  // set global variable from storage
  useEffect(() => {
    if (userSettings.fetchedFromStorage) {
      return;
    }

    const settings = getStorageItem(USER_SETTINGS_KEY);

    if (settings) {
      try {
        const settingsFromStorage = JSON.parse(settings);

        settingsFromStorage.fetchedFromStorage = true;
        setUserSettingsState(settingsFromStorage);
      } catch (error) {
        setUserSettingsState({ ...userSettings, fetchedFromStorage: true });
      }
    } else {
      setUserSettingsState({ ...userSettings, fetchedFromStorage: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSettings]);

  // keep local storage updated when settings state changes
  useEffect(() => {
    if (userSettings.fetchedFromStorage) {
      setStorageItem(USER_SETTINGS_KEY, JSON.stringify(userSettings));
    }
  }, [userSettings]);

  return userSettings;
};

export const useGetUserDefaultLocation = () => {
  const [userSettings] = useRecoilState(userSettingsState);

  return userSettings.locations.find(item => item.isDefault);
};

export const useGetUserWatchingLocation = () => {
  const [userSettings] = useRecoilState(userSettingsState);

  return userSettings.locations.find(item => item.isWatching);
};
